import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, CheckCircle2, Circle, ExternalLink, ChevronRight, ArrowUpDown, AlertTriangle, Check, Edit3, ArrowLeftRight, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/crm/UiBits";
import { resourceKindsFor } from "@/lib/tracks";
import TextGuestButton from "@/components/crm/TextGuestButton";
import { NS_MISSED_CLASS_TEXTS } from "@/lib/textExamples";

export const NS_LESSON_TITLES = [
  "Introduction", "The Bible: Our Daily Bread", "The Godhead, Part 1", "The Godhead, Part 2",
  "God's Eternal Plan: Redemption", "Repentance", "Baptism in Jesus' Name", "Receiving the Holy Ghost",
  "The 8 Levels of Spiritual Growth, Part 1", "The 8 Levels of Spiritual Growth, Part 2",
  "Trials and Burdens", "Enemies of the Soul", "Prayer", "Fasting", "Spiritual Authority",
  "Finances", "Praise and Worship", "The Holiness Lifestyle", "Scriptural Dress",
  "Standards of Adornment", "Hair is a Symbol", "Entertainment", "Getting Involved", "Graduation",
];

// Display + sort helpers. Legacy rosters may only have `name`; new records always
// have first_name/last_name populated. Sort key is (lastName, firstName) lowercased.
export const discipleDisplayName = (d) => {
  const fn = (d?.first_name || "").trim();
  const ln = (d?.last_name || "").trim();
  if (fn || ln) return `${fn} ${ln}`.trim();
  return (d?.name || "").trim();
};
export const discipleSortKey = (d) => {
  let ln = (d?.last_name || "").trim();
  let fn = (d?.first_name || "").trim();
  if (!ln && !fn && d?.name) {
    const parts = d.name.trim().split(/\s+/);
    ln = parts[parts.length - 1] || "";
    fn = parts.slice(0, -1).join(" ");
  }
  return `${ln.toLowerCase()}\u0000${fn.toLowerCase()}`;
};

// Sortable disciples × 24-lesson attendance matrix
export function AttendanceGrid({ disciples, sortKey, sortDir, onSort, streaks, maxCovered, busyTid, onToggle, onEdit }) {
  if (disciples.length === 0) {
    return <EmptyState icon={Users} title="No disciples yet" description="Add disciples to see the full attendance grid." testid="ns-grid-empty" />;
  }
  const SortHeader = ({ label, k, testid }) => (
    <button onClick={() => onSort(k)} className="flex items-center gap-1 text-[0.6rem] uppercase tracking-[0.18em] hover:text-[var(--ink)]" style={{ color: sortKey === k ? "var(--ink)" : "var(--muted)", fontWeight: sortKey === k ? 600 : 500 }} data-testid={testid}>
      {label} <ArrowUpDown size={10} />
      {sortKey === k && <span className="text-[0.55rem]">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );
  return (
    <div className="-mx-2 px-2" data-testid="ns-attendance-grid">
      <table className="w-full text-xs" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="sticky left-0 z-20 text-left px-3 py-2 min-w-[12rem]" style={{ top: "5.5rem", background: "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)", boxShadow: "0 1px 0 var(--border-subtle)" }}>
              <SortHeader label="Disciple" k="name" testid="ns-sort-name" />
            </th>
            {NS_LESSON_TITLES.map((title, i) => (
              <th key={i} className="sticky z-10 px-1 py-2 text-center min-w-[2.25rem]" style={{ top: "5.5rem", borderBottom: "1px solid var(--border-subtle)", background: i === maxCovered ? "var(--surface-hover)" : "var(--bg-alt)", boxShadow: "0 1px 0 var(--border-subtle)" }} title={title}>
                <div className="text-[0.6rem]" style={{ color: "var(--muted)" }}>L{i + 1}</div>
              </th>
            ))}
            <th className="sticky z-10 px-2 py-2 text-center min-w-[4rem]" style={{ top: "5.5rem", borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-alt)", boxShadow: "0 1px 0 var(--border-subtle)" }}>
              <SortHeader label="Total" k="attended" testid="ns-sort-attended" />
            </th>
            <th className="sticky z-10 px-2 py-2 text-center min-w-[4rem]" style={{ top: "5.5rem", borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-alt)", boxShadow: "0 1px 0 var(--border-subtle)" }}>
              <SortHeader label="Streak" k="streak" testid="ns-sort-streak" />
            </th>
          </tr>
        </thead>
        <tbody>
          {disciples.map((d) => {
            const totalPresent = (d.attendance || []).filter((a) => a?.completed).length;
            const streak = streaks[d.id] || 0;
            return (
              <tr key={d.id} data-testid={`ns-grid-row-${d.id}`} className="hover:bg-[var(--surface-hover)]">
                <td className="sticky left-0 px-3 py-2" style={{ background: streak >= 2 ? "rgba(191,90,66,0.06)" : "var(--bg-alt)", borderBottom: "1px solid var(--border-subtle)", borderLeft: streak >= 2 ? "3px solid var(--terracotta)" : "3px solid transparent" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate" style={{ color: "var(--ink)" }}>{discipleDisplayName(d)}</div>
                      <div className="text-[0.6rem] truncate" style={{ color: "var(--muted)" }}>{d.phone || d.email || ""}</div>
                    </div>
                    <button onClick={() => onEdit(d)} className="p-1 shrink-0 hover:opacity-70" style={{ color: "var(--ink-2)" }} data-testid={`ns-grid-edit-${d.id}`} title="Edit disciple">
                      <Edit3 size={12} />
                    </button>
                  </div>
                </td>
                {NS_LESSON_TITLES.map((title, i) => {
                  const att = d.attendance?.[i] || {};
                  const isBusy = busyTid === `${d.id}-${i}`;
                  return (
                    <td key={i} className="px-1 py-1 text-center" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <button onClick={() => onToggle(d, i)} disabled={isBusy} data-testid={`ns-grid-cell-${d.id}-${i}`} title={`L${i + 1}: ${title}${att.date ? ` · ${att.date}` : ""}`} className="inline-flex items-center justify-center w-6 h-6 rounded transition-colors" style={{ background: att.completed ? "var(--gold)" : "transparent", color: att.completed ? "#FFFFFF" : "var(--muted)", border: att.completed ? "none" : "1px solid var(--border-subtle)" }}>
                        {att.completed ? <CheckCircle2 size={12} /> : ""}
                      </button>
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-center" style={{ borderBottom: "1px solid var(--border-subtle)", color: "var(--ink-2)" }}>
                  <span className="font-medium" style={{ color: "var(--olive)" }}>{totalPresent}</span>
                  <span style={{ color: "var(--muted)" }}>/24</span>
                </td>
                <td className="px-2 py-2 text-center" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  {streak >= 2 ? (
                    <span className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-full" style={{ background: "var(--terracotta)", color: "#FFFFFF" }} data-testid={`ns-grid-streak-${d.id}`}>
                      <AlertTriangle size={10} /> {streak}
                    </span>
                  ) : (
                    <span style={{ color: "var(--muted)" }}>{streak || "—"}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ==================== LESSON RESOURCES LIBRARY (org-wide, all 24 lessons × 4 links) ====================

export function LessonResourcesLibrary({ orgId }) {
  const [resources, setResources] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    api.get("/coord/lesson-resources", { params: { org_id: orgId } })
      .then((r) => setResources(r.data.resources || {}))
      .catch(() => setResources({}))
      .finally(() => setLoading(false));
  }, [orgId]);

  return (
    <section className="mt-14 pt-10" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid="ns-resources-library">
      <div className="overline mb-1" style={{ fontSize: "0.6rem", color: "var(--gold)" }}>Lesson resources</div>
      <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Study library</h2>
      <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--muted)" }}>
        Curated study materials for each of the 24 lessons. Click any resource below to open it.
      </p>
      {loading ? (
        <p className="text-xs mt-6" style={{ color: "var(--muted)" }}>Loading resource library…</p>
      ) : (
        <div className="mt-6 space-y-2">
          {NS_LESSON_TITLES.map((title, i) => (
            <LessonResourceRow key={i} index={i} title={title} resources={resources} />
          ))}
        </div>
      )}
    </section>
  );
}

function LessonResourceRow({ index, title, resources }) {
  const kinds = resourceKindsFor("first_steps", index);
  const filled = kinds.filter((rk) => resources[`first_steps:${index}:${rk.key}`]).length;
  return (
    <div className="rounded-md p-3 flex flex-col md:flex-row md:items-center gap-3" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={`ns-resource-row-${index}`}>
      <div className="flex items-center gap-3 md:w-64 shrink-0">
        <span className="text-[0.65rem] uppercase tracking-wider font-medium shrink-0" style={{ color: filled === kinds.length ? "var(--gold)" : "var(--muted)" }}>L{index + 1}</span>
        <span className="text-sm truncate" style={{ color: "var(--ink)" }} title={title} data-testid={`ns-resource-title-${index}`}>{title}</span>
      </div>
      <div className={`grid grid-cols-2 gap-2 flex-1 w-full min-w-0 ${kinds.length === 5 ? "sm:grid-cols-3 md:grid-cols-5" : "md:grid-cols-4"}`}>
        {kinds.map((rk) => (
          <LinkBox key={rk.key} label={rk.label} url={resources[`first_steps:${index}:${rk.key}`] || ""} testid={`ns-resource-input-${index}-${rk.key}`} />
        ))}
      </div>
    </div>
  );
}

function LinkBox({ label, url, testid }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={testid} className="flex items-center justify-between gap-1 rounded-md border p-2 h-full hover:shadow-md transition" style={{ borderColor: "var(--gold)", background: "var(--surface-hover)" }} title={label}>
        <span className="text-[0.7rem] font-medium truncate" style={{ color: "var(--gold)", letterSpacing: "0.03em" }}>{label}</span>
        <ExternalLink size={11} style={{ color: "var(--gold)" }} className="shrink-0" />
      </a>
    );
  }
  return (
    <div className="rounded-md p-2 h-full w-full flex items-center justify-between gap-1" style={{ border: "1px dashed var(--border-subtle)", opacity: 0.55 }} data-testid={`${testid}-empty`} title={`${label} — not set yet`}>
      <span className="text-[0.7rem] truncate" style={{ color: "var(--muted)" }}>{label}</span>
      <span className="text-[0.55rem] italic shrink-0" style={{ color: "var(--muted)" }}>—</span>
    </div>
  );
}

export function LessonLinkBoxes({ index, resources }) {
  const kinds = resourceKindsFor("first_steps", index);
  return (
    <div className={`grid grid-cols-2 gap-2 w-full min-w-0 ${kinds.length === 5 ? "sm:grid-cols-3 lg:grid-cols-5" : "sm:grid-cols-4"}`} data-testid="ns-lesson-link-boxes">
      {kinds.map((rk) => (
        <LinkBox key={rk.key} label={rk.label} url={resources[`first_steps:${index}:${rk.key}`] || ""} testid={`ns-lesson-box-${rk.key}`} />
      ))}
    </div>
  );
}

export const nsNotesUrl = (resources, i) => resources?.[`first_steps:${i}:teacher_notes`] || "";

export function LessonTitleLink({ index, resources, className = "", testid }) {
  const title = NS_LESSON_TITLES[index];
  const url = nsNotesUrl(resources, index);
  if (!url) return <span className={className} style={{ color: "var(--ink)" }} title={title} data-testid={testid}>{title}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className={`${className} inline-flex items-center gap-1 hover:underline`} style={{ color: "var(--gold)" }} title={`${title} — open teacher notes`} data-testid={testid}>
      <span className="truncate">{title}</span> <ExternalLink size={11} className="shrink-0" />
    </a>
  );
}

const nextNsIndex = (d) => {
  const i = NS_LESSON_TITLES.findIndex((_, k) => !d.attendance?.[k]?.completed);
  return i < 0 ? null : i;
};

export function NsDiscipleRow({ d, orgId, churchName, lessonIndex, streak, busyTid, resources, onMark, onNote, onOpen, onMove, onRemove }) {
  const att = d.attendance?.[lessonIndex] || {};
  const totalPresent = (d.attendance || []).filter((a) => a?.completed).length;
  const next = nextNsIndex(d);
  const nextBusy = next !== null && busyTid === `${d.id}-${next}`;
  return (
    <div className="card-surface px-3 py-3 sm:px-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid={`ns-disciple-row-${d.id}`} style={streak >= 2 ? { borderLeft: "3px solid var(--terracotta)" } : undefined}>
      <button onClick={() => onMark(d, !att.completed)} disabled={busyTid === `${d.id}-${lessonIndex}`} className="shrink-0" data-testid={`ns-toggle-${d.id}`} aria-label={`Toggle L${lessonIndex + 1} attendance for ${discipleDisplayName(d)}`} title={`L${lessonIndex + 1} attendance`}>
        {att.completed ? <CheckCircle2 size={22} style={{ color: "var(--gold)" }} /> : <Circle size={22} style={{ color: "var(--muted)" }} />}
      </button>
      <div className="basis-[calc(100%-2.5rem)] sm:basis-auto sm:flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate" style={{ color: "var(--ink)" }}>{discipleDisplayName(d)}</span>
          <span className="text-[0.65rem]" style={{ color: "var(--olive)" }} data-testid={`ns-attended-${d.id}`}>{totalPresent}/24</span>
          {streak >= 2 && (
            <span className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-full" style={{ background: "var(--terracotta)", color: "#FFFFFF" }} data-testid={`ns-streak-badge-${d.id}`} title={`Missed the last ${streak} lessons`}>
              <AlertTriangle size={10} /> {streak} absent
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs flex items-center gap-1 min-w-0" style={{ color: "var(--muted)" }} data-testid={`ns-next-${d.id}`}>
          {next === null
            ? <span style={{ color: "var(--olive)" }}>All 24 lessons complete</span>
            : <>Next: <span className="shrink-0">L{next + 1}</span> <LessonTitleLink index={next} resources={resources} className="min-w-0" testid={`ns-next-link-${d.id}`} /></>}
        </div>
      </div>
      <TextGuestButton
        guest={{ id: d.id, phone: d.phone, first_name: (d.first_name || discipleDisplayName(d).split(" ")[0] || "").trim() }}
        orgId={orgId} logGuestId={d.guest_id || null} examples={NS_MISSED_CLASS_TEXTS} churchName={churchName}
        testid={`ns-text-${d.id}`}
      />
      {next !== null && (
        <button onClick={() => onMark(d, true, undefined, next)} disabled={nextBusy} className="shrink-0 h-9 px-3 rounded-full flex items-center gap-1.5 text-xs transition-colors text-[var(--olive)] hover:bg-[var(--olive)] hover:text-[var(--bg-base)]" style={{ border: "1px solid var(--olive)" }} title={`Mark L${next + 1} "${NS_LESSON_TITLES[next]}" done today`} aria-label={`Mark L${next + 1} done`} data-testid={`ns-markdone-${d.id}`}>
          <Check size={14} /> <span>{nextBusy ? "Saving…" : "Done"}</span>
        </button>
      )}
      <div className="flex items-center shrink-0">
        <button onClick={() => onOpen(d)} className="p-1.5" style={{ color: "var(--ink-2)" }} data-testid={`ns-edit-${d.id}`} title="Open contact card"><Edit3 size={14} /></button>
        <button onClick={() => onMove(d)} className="p-1.5" style={{ color: "var(--ink-2)" }} data-testid={`ns-move-${d.id}`} title="Move to another class"><ArrowLeftRight size={14} /></button>
        <button onClick={() => onRemove(d)} className="p-1.5" style={{ color: "var(--terracotta)" }} data-testid={`ns-remove-${d.id}`} title="Remove from class"><Trash2 size={14} /></button>
      </div>
      <div className="basis-full flex flex-col sm:flex-row gap-2 pl-8">
        {att.completed && (
          <input type="date" value={att.date || new Date().toISOString().slice(0, 10)} onChange={(e) => onMark(d, true, e.target.value)} className="text-xs px-2 py-0.5 sm:w-40" data-testid={`ns-date-${d.id}`} />
        )}
        <textarea key={lessonIndex} rows={1} placeholder={`L${lessonIndex + 1} notes…`} defaultValue={att.notes || ""} onBlur={(e) => { if (e.target.value !== (att.notes || "")) onNote(d, e.target.value); }} className="flex-1 text-xs px-2 py-1.5" data-testid={`ns-note-${d.id}`} />
      </div>
    </div>
  );
}

export function NsClassCard({ c, onOpen, onReopen }) {
  const totalCells = c.disciples.length * 24;
  const filled = c.disciples.reduce((s, d) => s + (d.attendance || []).filter((a) => a?.completed).length, 0);
  const finished = c.active === false;
  return (
    <div onClick={onOpen} className="card-surface p-5 cursor-pointer" style={finished ? { opacity: 0.85 } : undefined} data-testid={`ns-class-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-serif-display text-xl leading-tight" style={{ color: "var(--ink)" }}>{c.name}</h3>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {c.disciples.length} disciple{c.disciples.length === 1 ? "" : "s"}
            {c.teachers.length > 0 && <> · {c.teachers.map((t) => t.name).join(" & ")}</>}
            {finished && <> · Finished{c.finished_at ? ` ${new Date(c.finished_at).toLocaleDateString()}` : ""}</>}
          </div>
        </div>
        {onReopen ? (
          <button onClick={(e) => { e.stopPropagation(); onReopen(); }} className="text-xs px-3 py-1.5 rounded-full shrink-0 hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--border-strong)", color: "var(--ink-2)" }} data-testid={`ns-class-reopen-${c.id}`}>Reopen</button>
        ) : <ChevronRight size={16} style={{ color: "var(--gold)" }} />}
      </div>
      {c.disciples.length > 0 && (
        <div className="mt-4">
          <div className="text-[0.6rem] uppercase tracking-wider mb-1" style={{ color: "var(--muted)" }}>Overall attendance</div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-base)" }}>
            <div className="h-full rounded-full" style={{ width: totalCells > 0 ? `${(filled / totalCells) * 100}%` : "0%", background: "var(--gold)" }} />
          </div>
          <div className="text-[0.65rem] mt-1" style={{ color: "var(--muted)" }}>{filled} / {totalCells} check-ins recorded</div>
        </div>
      )}
    </div>
  );
}
